import React, {
  useState,
  useEffect,
  useMemo,
  createContext,
  useContext,
  useCallback,
} from "react";
import { PALETTES } from "../constants/palettes";
import { DEFAULT_PATHWAYS } from "../constants/pathwayDefaults";
import { setIconScale } from "../constants/appConfig";
import { CONTACT_LOG_TYPES } from "../constants/contactTypes";
import {
  STORAGE_KEY,
  SETTINGS_KEY,
  APP_NAME,
  APP_VERSION,
} from "../constants/appConfig";
import { isCfreHolder } from "../utils/memberHelpers";
import { debugLog } from "../utils/debugLog";
import { supabase } from "../lib/supabase";
import { memberService } from "../services/memberService";
import { toCamelCase } from "../utils/fieldMapper";
import { contactLogService } from "../services/contactLogService";
import { capacityService } from "../services/capacityService";
import { capacityWeightsService } from "../services/capacityWeightsService";
import { wealthProfileService } from "../services/wealthProfileService";
import { DEFAULT_CAPACITY_WEIGHTS } from "../utils/capacityScoring";
import { authService } from "../services/authService";
import { settingsService } from "../services/settingsService";
import { personaService } from "../services/personaService";
import { directorService } from "../services/directorService";
import { taskService } from "../services/taskService";
import { fundsService } from "../services/chapterSettingsService";
import { emailTemplatesService } from "../services/emailTemplatesService";
import { mergeTemplates } from "../constants/emailTemplates";
import {
  ROLE_TEMPLATES,
  getTemplate,
  buildDefaultPermissions,
} from "../constants/permissionDefaults";

// ═══════════════════════════════════════════════════════════════
// ClearView CRM App Context — Single Provider (Supabase-Connected)
// Organized in 8 sections per Modularization Blueprint
// ═══════════════════════════════════════════════════════════════

const AppContext = createContext(null);

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error("useApp must be used within AppProvider");
  return context;
};

// Default chapter settings — all receipt-related fields
const DEFAULT_CHAPTER_SETTINGS = {
  legalName: "",
  addressLine1: "",
  addressLine2: "",
  cityStateZip: "",
  ein: "",
  phone: "",
  email: "",
  logoUrl: "",
  signatureUrl: "",
  signatureName: "",
  signatureTitle: "",
  receiptPrefix: "",
  fiscalYearNote: "",
  fiscalYearStartMonth: 1,
  receiptThankYou: "",
  receiptThankYouDaf: "",
  receiptClosing: "",
  receiptDafNotice: "",
  receiptEmailSubject: "",
  receiptEmailBody: "",
  fontScale: 100, // percentage: 85 = compact, 100 = default, 115 = large, 130 = extra large
};

export const AppProvider = ({ children }) => {
  // ═══ SECTION 1: UI State ═══════════════════════════════════
  const [paletteId, setPaletteId] = useState("violet");
  const [viewMode, setViewMode] = useState("card");
  const [toolbarStyle, setToolbarStyle] = useState("icon-dot");
  const [sessionTimeout, setSessionTimeout] = useState(30); // minutes, 0 = never
  const [chapterName, setChapterName] = useState("PCPA");
  const [iconScale, setIconScaleState] = useState(() => {
    try {
      const cached = localStorage.getItem("cmm_icon_scale");
      const n = parseInt(cached, 10);
      return Number.isFinite(n) && n > 0 ? n : 100;
    } catch {
      return 100;
    }
  });
  // Apply the scale to the ICON_SIZES proxy synchronously so icons
  // rendered on this render already reflect the preference.
  setIconScale(iconScale);
  const [chapterSettings, setChapterSettings] = useState({
    ...DEFAULT_CHAPTER_SETTINGS,
  });
  const [emailTemplates, setEmailTemplates] = useState(() => mergeTemplates({}));
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState(null);

  const showLoading = useCallback((message = "Working on it...") => {
    setLoadingMessage(message);
  }, []);

  const hideLoading = useCallback(() => {
    setLoadingMessage(null);
  }, []);

  const palette = PALETTES[paletteId] || PALETTES.violet;

  const showMessage = useCallback((text, type = "success") => {
    const id = Date.now();
    setMessages((prev) => [...prev, { id, text, type }]);
    setTimeout(
      () => setMessages((prev) => prev.filter((m) => m.id !== id)),
      3500
    );
  }, []);

  // Changing icon scale: mutate the proxy's scale (so future renders
  // return scaled values), persist to localStorage, and update state
  // to trigger the React re-render that propagates everywhere.
  const updateIconScale = useCallback((pct) => {
    const n = Number.isFinite(pct) ? Math.round(pct) : 100;
    setIconScale(n);
    try {
      localStorage.setItem("cmm_icon_scale", String(n));
    } catch {
      /* ignore quota errors */
    }
    setIconScaleState(n);
  }, []);

  // ═══ SECTION 2: Auth State ═════════════════════════════════
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);

  const isAdmin = userProfile?.role === "admin";
  const isDirector = userProfile?.role === "director";
  const isVolunteer = ["event_manager"].includes(
    userProfile?.role
  );

  // ═══ SECTION 2b: Permission System ═════════════════════════
  const [rolePermissions, setRolePermissions] = useState(
    buildDefaultPermissions
  );

  // Central permission check — single source of truth
  const can = useCallback(
    (module) => {
      if (isAdmin) return true;
      const role = userProfile?.role;
      if (!role) return false;
      const roleConfig = rolePermissions[role];
      if (roleConfig?.permissions)
        return roleConfig.permissions[module] ?? false;
      const template = getTemplate(role);
      if (template) return template.permissions[module] ?? false;
      return false;
    },
    [isAdmin, userProfile, rolePermissions]
  );

  // Derived permission values (backward compatible)
  const canViewGifts = can("gifts");
  const canAddMembers = can("addMembers");
  const canEditMembers = can("editMembers");
  const canLogContacts = can("logContacts");
  const canFileContactReports = can("contactReports");

  // ═══ SECTION 3: Data State ═════════════════════════════════
  const [dataVersion, setDataVersion] = useState(0);
  const bumpDataVersion = useCallback(() => setDataVersion((v) => v + 1), []);

  const [members, setMembers] = useState([]);
  const [pathways, setPathways] = useState(DEFAULT_PATHWAYS);
  const [personas, setPersonas] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedMemberCapacity, setSelectedMemberCapacity] = useState(null);
  const [allCapacity, setAllCapacity] = useState({});
  const [allWealthProfiles, setAllWealthProfiles] = useState({});
  const [lastContactByMember, setLastContactByMember] = useState({});
  const [capacityWeights, setCapacityWeights] = useState({
    ...DEFAULT_CAPACITY_WEIGHTS,
  });
  const [navContext, setNavContext] = useState(null);
  const [cultivationQueue, setCultivationQueue] = useState([]);
  const [showCultivationQueue, setShowCultivationQueue] = useState(false);
  const [showTaskDrawer, setShowTaskDrawer] = useState(false);
  const [showQuickBar, setShowQuickBar] = useState(false);
  const [activePollenCardKey, setActivePollenCardKey] = useState(null);
  const [logContactRequestKey, setLogContactRequestKey] = useState(0);
  const [printContactReportId, setPrintContactReportId] = useState(null);
  const [allGifts, setAllGifts] = useState([]);
  const [allEventAttendance, setAllEventAttendance] = useState([]);
  const [allSponsorships, setAllSponsorships] = useState([]);
  const [directorTasks, setDirectorTasks] = useState([]);

  // Filters
  const [search, setSearch] = useState("");
  const [filterPathway, setFilterPathway] = useState("all");
  const [filterFlag, setFilterFlag] = useState("all");
  const [filterDirector, setFilterDirector] = useState("all");
  const [sortBy, setSortBy] = useState("lastName");
  const [showLapsed, setShowLapsed] = useState(false);
  const [lastReportTab, setLastReportTab] = useState("giving");

  // ═══ SECTION 4: Computed Values ════════════════════════════
  const [directors, setDirectors] = useState([]);
  const [funds, setFunds] = useState([]);
  const activeFunds = useMemo(() => funds.filter((f) => f.is_active !== false), [funds]);

  // Seed cultivation queue from profile when it loads
  useEffect(() => {
    if (userProfile?.cultivation_queue) {
      setCultivationQueue(userProfile.cultivation_queue);
    }
  }, [userProfile]);

  const lapsedCount = useMemo(
    () =>
      members.filter((m) => m.status === "lapsed" || m.status === "suspended")
        .length,
    [members]
  );

  // ── Director Tasks computed values ─────────────────────────
  const openTasks = useMemo(
    () => directorTasks.filter((t) => t.status === "open"),
    [directorTasks]
  );

  const taskBadgeCount = useMemo(() => {
    const todayStr = new Date().toISOString().split("T")[0];
    return openTasks.filter((t) => t.dueDate && t.dueDate <= todayStr).length;
  }, [openTasks]);

  const filteredMembers = useMemo(() => {
    // Status gate — hide lapsed/suspended unless toggle is on
    const statusFiltered = showLapsed
      ? members
      : members.filter((m) => (m.status || "active") === "active");

    return statusFiltered
      .filter((m) => {
        const query = search.toLowerCase();
        const matchSearch =
          !query ||
          `${m.firstName} ${m.lastName} ${m.credentialedFullName} ${m.organization} ${m.email} ${m.afpId}`
            .toLowerCase()
            .includes(query);
        const matchPath =
          filterPathway === "all" || m.pathway === filterPathway;
        const matchFlag =
          filterFlag === "all" ||
          (filterFlag === "members_only" && m.pathway !== "prospect") ||
          (filterFlag === "donor" && m.isDonor) ||
          (filterFlag === "board" && m.isBoardMember) ||
          (filterFlag === "cfre" && (m.isCfre || isCfreHolder(m))) ||
          (filterFlag === "prospect" && m.pathway === "prospect");
        const matchDir =
          filterDirector === "all" ||
          (filterDirector === "unassigned" &&
            (!m.assignedDirector || !m.assignedDirector.trim())) ||
          m.assignedDirector === filterDirector;
        return matchSearch && matchPath && matchFlag && matchDir;
      })
      .sort((a, b) => {
        const getValue = (obj, key) => (obj[key] || "").toLowerCase();
        const getSortName = (obj) =>
          (obj.lastName || obj.organization || obj.firstName || "").toLowerCase();
        if (sortBy === "lastName")
          return getSortName(a).localeCompare(getSortName(b));
        if (sortBy === "firstName") {
          const getFirst = (obj) =>
            (obj.firstName || obj.organization || obj.lastName || "").toLowerCase();
          return getFirst(a).localeCompare(getFirst(b));
        }
        if (sortBy === "organization")
          return getValue(a, "organization").localeCompare(
            getValue(b, "organization")
          );
        if (sortBy === "pathway")
          return (a.pathway || "").localeCompare(b.pathway || "");
        return 0;
      });
  }, [
    members,
    search,
    filterPathway,
    filterFlag,
    filterDirector,
    sortBy,
    showLapsed,
  ]);

  // ═══ SECTION 5: CRUD Operations (Supabase + Local State) ══

  const addMember = async (memberData) => {
    if (!memberData.firstName?.trim() && !memberData.lastName?.trim()) {
      debugLog.warn(
        "addMember",
        "Attempted to add member without name",
        memberData
      );
      showMessage("First or last name is required", "error");
      return;
    }
    try {
      const member = await memberService.create({
        ...memberData,
        pathway: memberData.pathway || "unengaged",
        isDonor: memberData.isDonor || false,
        isBoardMember: memberData.isBoardMember || false,
        assignedDirector: memberData.assignedDirector || "",
        notes: memberData.notes || "",
      });
      setMembers((prev) => [...prev, member]);
      showMessage(`${member.firstName} ${member.lastName} added`);
    } catch (error) {
      debugLog.error("addMember", error);
      showMessage("Error adding member — " + error.message, "error");
    }
  };

  const updateMember = async (id, updates, silent = false) => {
    if (!id) {
      debugLog.warn("updateMember", "Called without member ID");
      return;
    }
    // Optimistic update — apply locally first
    setMembers((prev) => {
      const exists = prev.find((m) => m.id === id);
      if (!exists) {
        debugLog.warn(
          "updateMember",
          `Member ${id} not found in members array`
        );
        return prev;
      }
      return prev.map((m) =>
        m.id === id
          ? { ...m, ...updates, updatedAt: new Date().toISOString() }
          : m
      );
    });
    if (selectedMember?.id === id)
      setSelectedMember((prev) => ({ ...prev, ...updates }));
    if (!silent) showMessage("Member updated");

    // Persist to Supabase in background
    try {
      await memberService.update(id, updates);
    } catch (error) {
      debugLog.error("updateMember persist", error);
    }
  };

  const deleteMember = async (id) => {
    if (!id) {
      debugLog.warn("deleteMember", "Called without member ID");
      return;
    }
    try {
      await memberService.remove(id);
      setMembers((prev) => prev.filter((m) => m.id !== id));
      debugLog.action("Member deleted", { id });
      showMessage("Member removed", "warning");
    } catch (error) {
      debugLog.error("deleteMember", error);
      showMessage("Error deleting member", "error");
    }
  };

  const refreshMember = async (id) => {
    if (!id) return;
    try {
      const { data, error } = await supabase
        .from("members")
        .select("*, contact_logs(*)")
        .eq("id", id)
        .single();
      if (error) throw error;
      const fresh = { ...toCamelCase(data), _logsLoaded: true };
      setMembers((prev) => prev.map((m) => (m.id === id ? fresh : m)));
      if (selectedMember?.id === id) setSelectedMember(fresh);
    } catch (error) {
      debugLog.error("refreshMember", error);
    }
  };

  const addContactLog = async (memberId, entry) => {
    if (!memberId) {
      debugLog.warn("addContactLog", "Called without member ID");
      return null;
    }
    const validType = CONTACT_LOG_TYPES.find((t) => t.id === entry.type);
    try {
      const logEntry = await contactLogService.create(memberId, entry);
      setMembers((prev) =>
        prev.map((m) => {
          if (m.id === memberId) {
            const updated = {
              ...m,
              contactLog: [logEntry, ...(m.contactLog || [])],
              updatedAt: new Date().toISOString(),
            };
            if (selectedMember?.id === memberId) setSelectedMember(updated);
            return updated;
          }
          return m;
        })
      );
      showMessage(`${validType?.name || "Contact"} logged`);
      return logEntry;
    } catch (error) {
      debugLog.error("addContactLog", error);
      showMessage("Error logging contact", "error");
      return null;
    }
  };

  const updateContactLog = async (memberId, logId, updates) => {
    if (!memberId || !logId) {
      debugLog.warn("updateContactLog", "Called without member ID or log ID");
      return null;
    }
    try {
      const updated = await contactLogService.update(logId, updates);
      setMembers((prev) =>
        prev.map((m) => {
          if (m.id === memberId) {
            const refreshed = {
              ...m,
              contactLog: (m.contactLog || []).map((l) =>
                l.id === logId ? { ...l, ...updated } : l
              ),
              updatedAt: new Date().toISOString(),
            };
            if (selectedMember?.id === memberId) setSelectedMember(refreshed);
            return refreshed;
          }
          return m;
        })
      );
      showMessage("Contact log updated");
      return updated;
    } catch (error) {
      debugLog.error("updateContactLog", error);
      showMessage("Error updating contact log", "error");
      return null;
    }
  };

  const deleteContactLog = async (memberId, logId) => {
    if (!memberId || !logId) {
      debugLog.warn("deleteContactLog", "Called without member ID or log ID", {
        memberId,
        logId,
      });
      return;
    }
    try {
      await contactLogService.remove(logId);
      setMembers((prev) =>
        prev.map((m) => {
          if (m.id === memberId) {
            const updated = {
              ...m,
              contactLog: (m.contactLog || []).filter((l) => l.id !== logId),
              updatedAt: new Date().toISOString(),
            };
            if (selectedMember?.id === memberId) setSelectedMember(updated);
            return updated;
          }
          return m;
        })
      );
      showMessage("Log entry removed", "warning");
    } catch (error) {
      debugLog.error("deleteContactLog", error);
      showMessage("Error removing log entry", "error");
    }
  };

  // ── Cultivation Queue ──────────────────────────────────────
  const persistQueue = async (newQueue) => {
    if (!currentUser?.id) return;
    try {
      const { error } = await supabase
        .from("profiles")
        .update({ cultivation_queue: newQueue })
        .eq("id", currentUser.id);
      if (error) debugLog.error("persistQueue", error);
    } catch (err) {
      debugLog.error("persistQueue", err);
    }
  };

  const addToQueue = (memberId, source = "manual") => {
    setCultivationQueue((prev) => {
      if (prev.some((q) => q.memberId === memberId)) return prev;
      const newQueue = [
        { memberId, source, addedAt: new Date().toISOString() },
        ...prev,
      ];
      persistQueue(newQueue);
      return newQueue;
    });
    showMessage("Added to cultivation queue");
  };

  const removeFromQueue = (memberId) => {
    setCultivationQueue((prev) => {
      const newQueue = prev.filter((q) => q.memberId !== memberId);
      persistQueue(newQueue);
      return newQueue;
    });
  };

  const clearCompletedFromQueue = (doneIds) => {
    setCultivationQueue((prev) => {
      const newQueue = prev.filter((q) => !doneIds.includes(q.memberId));
      persistQueue(newQueue);
      return newQueue;
    });
    showMessage("Cleared completed items");
  };

  // ── Director Tasks CRUD ────────────────────────────────────
  const addTask = async (taskData) => {
    try {
      const task = await taskService.create(taskData);
      setDirectorTasks((prev) => [task, ...prev]);
      showMessage("Task created");
      return task;
    } catch (error) {
      debugLog.error("addTask", error);
      showMessage("Error creating task", "error");
      return null;
    }
  };

  const updateTask = async (id, updates) => {
    // Optimistic update
    setDirectorTasks((prev) =>
      prev.map((t) =>
        t.id === id ? { ...t, ...updates, updatedAt: new Date().toISOString() } : t
      )
    );
    try {
      await taskService.update(id, updates);
    } catch (error) {
      debugLog.error("updateTask", error);
      showMessage("Error updating task", "error");
    }
  };

  const completeTask = async (id) => {
    const now = new Date().toISOString();
    setDirectorTasks((prev) =>
      prev.map((t) =>
        t.id === id ? { ...t, status: "completed", completedAt: now } : t
      )
    );
    showMessage("Task completed");
    try {
      const result = await taskService.complete(id);
      if (result?.spawned) {
        setDirectorTasks((prev) => [result.spawned, ...prev]);
        showMessage(
          `Next occurrence scheduled for ${result.spawned.dueDate || "no due date"}`
        );
      }
    } catch (error) {
      debugLog.error("completeTask", error);
    }
  };

  const deleteTask = async (id) => {
    const target = directorTasks.find((t) => t.id === id);
    const pausesSeries =
      !!target && target.status === "open" && !!target.recurrenceId;
    setDirectorTasks((prev) => prev.filter((t) => t.id !== id));
    showMessage(
      pausesSeries ? "Task removed — series ended" : "Task removed",
      "warning"
    );
    try {
      await taskService.remove(id);
    } catch (error) {
      debugLog.error("deleteTask", error);
      showMessage("Error removing task", "error");
    }
  };

  const updateSeries = async (recurrenceId, updates) => {
    try {
      await taskService.updateSeries(recurrenceId, updates);
      showMessage("Series template updated");
    } catch (error) {
      debugLog.error("updateSeries", error);
      showMessage("Error updating series", "error");
    }
  };

  const pauseRecurrence = async (recurrenceId) => {
    try {
      await taskService.pauseRecurrence(recurrenceId);
      showMessage("Series ended");
    } catch (error) {
      debugLog.error("pauseRecurrence", error);
      showMessage("Error ending series", "error");
    }
  };

  const loadTasksWithArchive = async () => {
    try {
      const all = await taskService.getAllWithArchive();
      setDirectorTasks(all);
    } catch (error) {
      debugLog.error("loadTasksWithArchive", error);
      showMessage("Error loading archive", "error");
    }
  };

  const loadTasksStandard = async () => {
    try {
      const rows = await taskService.getAll();
      setDirectorTasks(rows);
    } catch (error) {
      debugLog.error("loadTasksStandard", error);
    }
  };

  const importMembers = async (newMembers) => {
    setLoadingMessage("Importing members...");
    try {
      const result = await memberService.importBulk(newMembers);
      const freshMembers = await memberService.getAll();
      setMembers(freshMembers);
      showMessage(`Imported: ${result.added} new, ${result.updated} updated`);
    } catch (error) {
      debugLog.error("importMembers", error);
      showMessage("Error importing members — " + error.message, "error");
    } finally {
      setLoadingMessage(null);
    }
  };

  const addPathway = async (pathway) => {
    try {
      await settingsService.addPathway(pathway);
      setPathways((prev) => [...prev, pathway]);
      showMessage(`"${pathway.name}" pathway added`);
    } catch (error) {
      debugLog.error("addPathway", error);
      showMessage("Error adding pathway", "error");
    }
  };

  const removePathway = async (pathwayId) => {
    const pw = pathways.find((p) => p.id === pathwayId);
    try {
      await settingsService.removePathway(pathwayId);
      setPathways((prev) => prev.filter((p) => p.id !== pathwayId));
      showMessage(`"${pw?.name || pathwayId}" pathway removed`);
    } catch (error) {
      debugLog.error("removePathway", error);
      showMessage("Error removing pathway", "error");
    }
  };

  const addPersona = async (personaData) => {
    try {
      const persona = await personaService.create(personaData);
      setPersonas((prev) => [...prev, persona]);
      showMessage(`"${persona.name}" persona added`);
      return persona;
    } catch (error) {
      debugLog.error("addPersona", error);
      showMessage("Error adding persona — " + error.message, "error");
      return null;
    }
  };

  const updatePersona = async (id, updates) => {
    try {
      const updated = await personaService.update(id, updates);
      setPersonas((prev) => prev.map((p) => (p.id === id ? updated : p)));
      showMessage("Persona updated");
      return updated;
    } catch (error) {
      debugLog.error("updatePersona", error);
      showMessage("Error updating persona", "error");
      return null;
    }
  };

  const removePersona = async (id) => {
    const persona = personas.find((p) => p.id === id);
    try {
      await personaService.remove(id);
      setPersonas((prev) => prev.filter((p) => p.id !== id));
      showMessage(`"${persona?.name || id}" persona removed`, "warning");
    } catch (error) {
      debugLog.error("removePersona", error);
      showMessage("Error removing persona", "error");
    }
  };

  // ═══ SECTION 6: Data Persistence ═══════════════════════════

  const handleLogin = async (email, password) => {
    try {
      const { user, profile } = await authService.signIn(email, password);
      setCurrentUser(user);
      setUserProfile(profile);
      setIsLoggedIn(true);
      await loadFromSupabase();
      return { success: true };
    } catch (error) {
      debugLog.error("handleLogin", error);
      return { success: false, error: error.message };
    }
  };

  const handleLogout = async () => {
    try {
      await authService.signOut();
    } catch (error) {
      debugLog.error("handleLogout", error);
    }
    setCurrentUser(null);
    setUserProfile(null);
    setIsLoggedIn(false);
    setMembers([]);
    setAllGifts([]);
    setAllEventAttendance([]);
    setAllSponsorships([]);
    setDirectorTasks([]);
    setCurrentView("login");
    setViewHistory([]);
  };

  const loadFromSupabase = useCallback(async () => {
    setLoadingMessage("Loading your data...");
    try {
      const dbMembers = await memberService.getAll();
      setMembers(dbMembers);

      // Load all gifts (cursor-paginated) for centralized caching
      try {
        let fetchedGifts = [];
        let lastGiftId = null;
        const giftPageSize = 1000;
        while (true) {
          let query = supabase
            .from("gifts")
            .select("id, member_id, amount, gift_date, fund_id")
            .order("id", { ascending: true })
            .limit(giftPageSize);
          if (lastGiftId) query = query.gt("id", lastGiftId);
          const { data, error } = await query;
          if (error || !data || data.length === 0) break;
          fetchedGifts = fetchedGifts.concat(data);
          lastGiftId = data[data.length - 1].id;
          if (data.length < giftPageSize) break;
        }
        setAllGifts(fetchedGifts);
      } catch (err) {
        debugLog.error("loadFromSupabase gifts", err);
      }

      // Load event attendance with event dates (for engagement scoring)
      try {
        const { data: attendance, error } = await supabase
          .from("event_attendance")
          .select("member_id, attended, chapter_events (event_date)")
          .eq("attended", true);
        if (!error && attendance) {
          setAllEventAttendance(
            attendance
              .filter((a) => a.chapter_events?.event_date)
              .map((a) => ({ member_id: a.member_id, event_date: a.chapter_events.event_date }))
          );
        }
      } catch (err) {
        debugLog.error("loadFromSupabase eventAttendance", err);
      }

      // Load show sponsorships (for engagement scoring)
      try {
        const { data: sponsorships, error } = await supabase
          .from("show_sponsorships")
          .select("member_id, created_at, shows (open_date)");
        if (!error && sponsorships) {
          setAllSponsorships(
            sponsorships.map((s) => ({
              member_id: s.member_id,
              date: s.shows?.open_date || s.created_at?.split("T")[0],
            }))
          );
        }
      } catch (err) {
        debugLog.error("loadFromSupabase sponsorships", err);
      }

      const dbPathways = await settingsService.getPathways();
      if (dbPathways.length > 0) setPathways(dbPathways);

      const dbPersonas = await personaService.getAll();
      setPersonas(dbPersonas);

      const dbDirectors = await directorService.getActive();
      setDirectors(dbDirectors);

      // Load director tasks
      try {
        const dbTasks = await taskService.getAll();
        setDirectorTasks(dbTasks);
      } catch (err) {
        debugLog.error("loadFromSupabase tasks", err);
      }

      const dbFunds = await fundsService.getAll();
      setFunds(dbFunds);

      const settings = await settingsService.getAll();
      if (settings.chapter_name) setChapterName(settings.chapter_name);
      if (settings.default_palette) setPaletteId(settings.default_palette);
      if (settings.custom_fields) setCustomFields(settings.custom_fields);
      if (settings.chapter_settings) {
        setChapterSettings((prev) => ({
          ...DEFAULT_CHAPTER_SETTINGS,
          ...settings.chapter_settings,
        }));
      }
      setEmailTemplates(mergeTemplates(settings.email_templates || {}));

      // Capacity weights: chapter override of the 6-component blend
      if (settings.capacity_weights) {
        setCapacityWeights({
          ...DEFAULT_CAPACITY_WEIGHTS,
          ...settings.capacity_weights,
        });
      }

      // Bulk-load portfolio data needed by CapacityView. Silent on
      // failure — the view still renders, it just won't have data.
      // RLS gates these tables, so non-fundraising users will get
      // empty objects naturally.
      try {
        const [capacityMap, latestContactMap, wealthMap] = await Promise.all([
          capacityService.getAll(),
          contactLogService.getLatestDatesByMember(),
          wealthProfileService.getAll(),
        ]);
        setAllCapacity(capacityMap);
        setLastContactByMember(latestContactMap);
        setAllWealthProfiles(wealthMap);
      } catch (err) {
        debugLog.error("loadFromSupabase capacity", err);
      }

      if (settings.role_permissions) {
        setRolePermissions((prev) => {
          const merged = { ...prev };
          Object.keys(settings.role_permissions).forEach((roleId) => {
            const template = getTemplate(roleId);
            if (template && !template.locked) {
              merged[roleId] = {
                template: settings.role_permissions[roleId].template || roleId,
                customized:
                  settings.role_permissions[roleId].customized || false,
                permissions: {
                  ...template.permissions,
                  ...settings.role_permissions[roleId].permissions,
                },
              };
            }
          });
          return merged;
        });
      }

      // Cache to localStorage as fallback
      try {
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({
            members: dbMembers,
            lastUpdated: new Date().toISOString(),
          })
        );
      } catch (e) {
        /* not critical */
      }

      debugLog.data("Data loaded from Supabase", {
        members: dbMembers.length,
        pathways: dbPathways.length,
        personas: dbPersonas.length,
      });
      setHasLoaded(true);
      setLoadingMessage(null);

      // Update presence timestamp for admin visibility
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (session?.user?.id) {
          await supabase
            .from("profiles")
            .update({ last_active_at: new Date().toISOString() })
            .eq("id", session.user.id);
        }
      } catch (e) {
        // Presence ping is non-critical — don't block on failure
      }
    } catch (error) {
      debugLog.error("loadFromSupabase", error);
      setLoadingMessage(null);
      // Fall back to localStorage cache
      try {
        const cached = localStorage.getItem(STORAGE_KEY);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (parsed.members) {
            setMembers(parsed.members);
            debugLog.data("Loaded from localStorage cache", {
              count: parsed.members.length,
            });
            showMessage("Offline mode — showing cached data", "warning");
          }
        }
      } catch (e) {
        debugLog.error("localStorage fallback", e);
      }
    }
  }, [showMessage]);

  // Initialize — check auth session on mount
  useEffect(() => {
    const init = async () => {
      setIsLoading(true);

      // Detect invite or recovery links (URL hash contains type=invite or type=recovery)
      const hash = window.location.hash || "";
      const isInviteFlow =
        hash.includes("type=invite") || hash.includes("type=signup");
      const isRecoveryFlow = hash.includes("type=recovery");
      const needsPasswordSetup = isInviteFlow || isRecoveryFlow;

      try {
        const result = await authService.getCurrentUser();
        if (result) {
          setCurrentUser(result.user);
          setUserProfile(result.profile);
          setIsLoggedIn(true);
          await loadFromSupabase();

          if (needsPasswordSetup) {
            // User arrived via invite or recovery link — prompt to set password
            // Clean the hash so a page refresh doesn't re-trigger
            window.history.replaceState(null, "", window.location.pathname);
            setCurrentView("setPassword");
          } else {
            // Restore previous view if session timed out, otherwise go to main
            let restoreView = null;
            try {
              restoreView = sessionStorage.getItem("cmm_restore_view");
              sessionStorage.removeItem("cmm_restore_view");
            } catch (e) { /* not critical */ }
            setCurrentView(restoreView || "main");
          }
        } else {
          // No session — load UI preferences from localStorage
          try {
            const savedSettings = localStorage.getItem(SETTINGS_KEY);
            if (savedSettings) {
              const settings = JSON.parse(savedSettings);
              if (settings.paletteId) setPaletteId(settings.paletteId);
              if (settings.toolbarStyle) setToolbarStyle(settings.toolbarStyle);
              if (settings.sessionTimeout != null) setSessionTimeout(settings.sessionTimeout);
            }
          } catch (e) {
            /* not critical */
          }
          setCurrentView("login");
        }
      } catch (error) {
        debugLog.error("init", error);
        setCurrentView("login");
      }
      setIsLoading(false);
    };

    init();

    // Listen for auth changes
    const unsubscribe = authService.onAuthStateChange(
      async (event, session) => {
        if (event === "SIGNED_OUT") {
          setIsLoggedIn(false);
          setCurrentUser(null);
          setUserProfile(null);
          setCurrentView("login");
        } else if (event === "SIGNED_IN" && session) {
          const profile = await authService.getProfile(session.user.id);
          setCurrentUser(session.user);
          setUserProfile(profile);
          setIsLoggedIn(true);
        }
      }
    );

    return unsubscribe;
  }, [loadFromSupabase]);

  // ── Inactivity auto-logout timer ─────────────────────────────
  const lastActivityRef = React.useRef(Date.now());

  useEffect(() => {
    if (!isLoggedIn || sessionTimeout === 0) return;

    // Reset activity timestamp when timer (re)starts so settings load doesn't cause premature logout
    lastActivityRef.current = Date.now();

    const markActive = () => { lastActivityRef.current = Date.now(); };
    const events = ["mousedown", "keydown", "touchstart", "scroll"];
    events.forEach((evt) => window.addEventListener(evt, markActive, { passive: true }));

    const interval = setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current;
      if (elapsed >= sessionTimeout * 60 * 1000) {
        // Save current view for session restore after re-login
        try {
          sessionStorage.setItem("cmm_restore_view", currentView);
        } catch (e) { /* not critical */ }
        showMessage("Session expired due to inactivity", "warning");
        handleLogout();
      }
    }, 15000); // check every 15 seconds

    return () => {
      clearInterval(interval);
      events.forEach((evt) => window.removeEventListener(evt, markActive));
    };
  }, [isLoggedIn, sessionTimeout]); // handleLogout and showMessage are stable within a session

  // Track whether initial load is complete (prevents overwriting Supabase on mount)
  const [hasLoaded, setHasLoaded] = useState(false);

  // Save UI preferences to localStorage (personal, not shared)
  useEffect(() => {
    try {
      localStorage.setItem(
        SETTINGS_KEY,
        JSON.stringify({ paletteId, viewMode, toolbarStyle, sessionTimeout })
      );
    } catch (error) {
      debugLog.error("localStorage save UI prefs", error);
    }
  }, [paletteId, viewMode, toolbarStyle, sessionTimeout]);

  // Save shared settings to Supabase when they change (admin only)
  useEffect(() => {
    if (!hasLoaded || !isLoggedIn || !isAdmin) return;
    settingsService
      .set("chapter_name", chapterName)
      .catch((e) => debugLog.error("Save chapter_name", e));
  }, [chapterName, hasLoaded, isLoggedIn, isAdmin]);

  useEffect(() => {
    if (!hasLoaded || !isLoggedIn || !isAdmin) return;
    settingsService
      .set("default_palette", paletteId)
      .catch((e) => debugLog.error("Save default_palette", e));
  }, [paletteId, hasLoaded, isLoggedIn, isAdmin]);

  useEffect(() => {
    if (!hasLoaded || !isLoggedIn || !isAdmin) return;
    settingsService
      .set("custom_fields", customFields)
      .catch((e) => debugLog.error("Save custom_fields", e));
  }, [customFields, hasLoaded, isLoggedIn, isAdmin]);

  // Save chapter settings to Supabase when they change (admin only)
  useEffect(() => {
    if (!hasLoaded || !isLoggedIn || !isAdmin) return;
    settingsService
      .set("chapter_settings", chapterSettings)
      .catch((e) => debugLog.error("Save chapter_settings", e));
  }, [chapterSettings, hasLoaded, isLoggedIn, isAdmin]);

  // Save role permissions to Supabase when they change (admin only)
  useEffect(() => {
    if (!hasLoaded || !isLoggedIn || !isAdmin) return;
    settingsService
      .set("role_permissions", rolePermissions)
      .catch((e) => debugLog.error("Save role_permissions", e));
  }, [rolePermissions, hasLoaded, isLoggedIn, isAdmin]);

  // ═══ SECTION 7: Navigation ═════════════════════════════════
  const [currentView, setCurrentView] = useState("login");
  const [viewHistory, setViewHistory] = useState([]);

  // Lazy-load contact logs + capacity when a member is selected
  const selectMemberWithLogs = useCallback(async (member) => {
    if (!member) {
      setSelectedMember(null);
      setSelectedMemberCapacity(null);
      return;
    }
    // Set immediately so UI is responsive
    setSelectedMember(member);
    setSelectedMemberCapacity(null);
    // Always fetch fresh contact logs on selection to avoid stale data
    try {
      const [logs, capacity] = await Promise.all([
        contactLogService.getByMember(member.id),
        capacityService.getByMember(member.id),
      ]);
      const enriched = { ...member, contactLog: logs, _logsLoaded: true };
      setSelectedMember(enriched);
      setSelectedMemberCapacity(capacity);
      // Also update in the members array so it's cached
      setMembers((prev) =>
        prev.map((m) => (m.id === member.id ? enriched : m))
      );
    } catch (err) {
      debugLog.error("selectMemberWithLogs", err);
    }
  }, []);

  // Upsert the selected donor's wealth profile (manual research from
  // the Capacity tab). Source defaults to "manual". Keeps the global
  // allWealthProfiles map in sync so the Capacity tab and top-level
  // CapacityView both pick up the change immediately.
  const upsertSelectedMemberWealthProfile = useCallback(
    async (patch, source = "manual") => {
      if (!selectedMember?.id) return null;
      try {
        const updated = await wealthProfileService.upsert(
          selectedMember.id,
          patch,
          currentUser?.id,
          source
        );
        if (updated) {
          setAllWealthProfiles((prev) => ({
            ...prev,
            [selectedMember.id]: updated,
          }));
        }
        return updated;
      } catch (err) {
        debugLog.error("upsertSelectedMemberWealthProfile", err);
        throw err;
      }
    },
    [selectedMember?.id, currentUser?.id]
  );

  const refreshWealthProfiles = useCallback(async () => {
    try {
      const map = await wealthProfileService.getAll();
      setAllWealthProfiles(map);
      return map;
    } catch (err) {
      debugLog.error("refreshWealthProfiles", err);
      return {};
    }
  }, []);

  const upsertSelectedMemberCapacity = useCallback(
    async (patch) => {
      if (!selectedMember?.id) return null;
      try {
        const updated = await capacityService.upsert(
          selectedMember.id,
          patch,
          currentUser?.id
        );
        setSelectedMemberCapacity(updated);
        if (updated) {
          setAllCapacity((prev) => ({
            ...prev,
            [selectedMember.id]: updated,
          }));
        }
        return updated;
      } catch (err) {
        debugLog.error("upsertSelectedMemberCapacity", err);
        throw err;
      }
    },
    [selectedMember?.id, currentUser?.id]
  );

  const navigate = (view, member = null, context = null) => {
    setViewHistory((prev) => [...prev, currentView]);
    setCurrentView(view);
    setNavContext(context);
    if (member !== null) selectMemberWithLogs(member);
  };

  const goBack = () => {
    const prev = viewHistory[viewHistory.length - 1];
    setViewHistory((history) => history.slice(0, -1));
    setCurrentView(prev || "main");
  };

  const goHome = () => {
    setViewHistory([]);
    setCurrentView("main");
  };

  const navigateToMemberByOffset = (offset) => {
    if (!selectedMember || filteredMembers.length === 0) return;
    const currentIndex = filteredMembers.findIndex(
      (m) => m.id === selectedMember.id
    );
    if (currentIndex === -1) return;
    const nextIndex = currentIndex + offset;
    if (nextIndex < 0 || nextIndex >= filteredMembers.length) return;
    const nextMember = filteredMembers[nextIndex];
    selectMemberWithLogs(nextMember);
    debugLog.action("Record nav", {
      direction: offset > 0 ? "next" : "prev",
      name: `${nextMember.firstName} ${nextMember.lastName}`,
    });
  };

  const navigateToNextMember = () => navigateToMemberByOffset(1);
  const navigateToPrevMember = () => navigateToMemberByOffset(-1);

  const memberPosition = selectedMember
    ? filteredMembers.findIndex((m) => m.id === selectedMember.id) + 1
    : 0;

  // ═══ SECTION 8: Provider Value ═════════════════════════════
  const providerValue = useMemo(() => ({
        // UI
        palette,
        paletteId,
        setPaletteId,
        viewMode,
        setViewMode,
        toolbarStyle,
        setToolbarStyle,
        sessionTimeout,
        setSessionTimeout,
        chapterName,
        setChapterName,
        iconScale,
        setIconScale: updateIconScale,
        chapterSettings,
        setChapterSettings,
        emailTemplates,
        setEmailTemplates,
        isLoggedIn,
        setIsLoggedIn,
        isLoading,
        loadingMessage,
        showLoading,
        hideLoading,
        showMessage,
        messages,
        // Auth
        currentUser,
        userProfile,
        isAdmin,
        isDirector,
        isVolunteer,
        canViewGifts,
        canAddMembers,
        canEditMembers,
        canLogContacts,
        canFileContactReports,
        can,
        rolePermissions,
        setRolePermissions,
        handleLogin,
        handleLogout,
        // Data
        members,
        setMembers,
        selectedMember,
        setSelectedMember,
        selectedMemberCapacity,
        upsertSelectedMemberCapacity,
        allCapacity,
        allWealthProfiles,
        refreshWealthProfiles,
        upsertSelectedMemberWealthProfile,
        lastContactByMember,
        capacityWeights,
        setCapacityWeights,
        navContext,
        pathways,
        setPathways,
        personas,
        customFields,
        setCustomFields,
        directors,
        setDirectors,
        funds,
        setFunds,
        activeFunds,
        allGifts,
        allEventAttendance,
        allSponsorships,
        filteredMembers,
        // CRUD
        addMember,
        updateMember,
        deleteMember,
        importMembers,
        addContactLog,
        updateContactLog,
        deleteContactLog,
        cultivationQueue,
        showCultivationQueue,
        setShowCultivationQueue,
        showTaskDrawer,
        setShowTaskDrawer,
        showQuickBar,
        setShowQuickBar,
        activePollenCardKey,
        setActivePollenCardKey,
        logContactRequestKey,
        triggerLogContact: () => setLogContactRequestKey((k) => k + 1),
        printContactReportId,
        setPrintContactReportId,
        addToQueue,
        removeFromQueue,
        clearCompletedFromQueue,
        // Director Tasks
        directorTasks,
        openTasks,
        taskBadgeCount,
        addTask,
        updateTask,
        completeTask,
        deleteTask,
        updateSeries,
        pauseRecurrence,
        loadTasksWithArchive,
        loadTasksStandard,
        addPathway,
        removePathway,
        // Personas
        addPersona,
        updatePersona,
        removePersona,
        // Filters
        search,
        setSearch,
        filterPathway,
        setFilterPathway,
        filterFlag,
        setFilterFlag,
        filterDirector,
        setFilterDirector,
        sortBy,
        setSortBy,
        showLapsed,
        setShowLapsed,
        lapsedCount,
        lastReportTab,
        setLastReportTab,
        // Navigation
        currentView,
        navigate,
        goBack,
        goHome,
        navigateToNextMember,
        navigateToPrevMember,
        memberPosition,
        // Supabase utilities
        loadFromSupabase,
        refreshMember,
        dataVersion,
        bumpDataVersion,
  }), [
    palette, paletteId, viewMode, toolbarStyle, sessionTimeout,
    chapterName, iconScale, chapterSettings, emailTemplates, isLoggedIn, isLoading, loadingMessage,
    currentUser, userProfile, isAdmin, isDirector, isVolunteer,
    canViewGifts, canAddMembers, canEditMembers, canLogContacts, canFileContactReports,
    rolePermissions, members, selectedMember, selectedMemberCapacity, allCapacity, allWealthProfiles, lastContactByMember, capacityWeights, navContext, pathways,
    personas, customFields, directors, funds, activeFunds, allGifts, allEventAttendance, allSponsorships,
    directorTasks, openTasks, taskBadgeCount,
    filteredMembers, cultivationQueue, showCultivationQueue, showTaskDrawer, showQuickBar, logContactRequestKey, printContactReportId, search, filterPathway, filterFlag,
    filterDirector, sortBy, showLapsed, lapsedCount, lastReportTab, currentView,
    memberPosition, dataVersion, messages,
  ]);

  return (
    <AppContext.Provider value={providerValue}>
      {children}
    </AppContext.Provider>
  );
};
